/*
 * Student-profile sales tasks panel: Open/Closed/All split, status chip tones and
 * the per-row rule and meta lines. Uses only fields /student_tasks already returns.
 */

import { is_terminal } from '../../util_components/instant_alert/task_status';
import { createdListLine } from './created_time';
import type { Tone } from './ui';

export type ProfileFilter = 'open' | 'closed' | 'all';

export interface ProfileTask {
  exists?: boolean;
  status?: string;
  is_instant?: boolean;
  is_lead?: boolean;
  rule_day?: number | null;
  level?: string | null;
  created_at_iso?: string | null;
}

// Placeholder rows ("Not created", offered with Create) are actionable, so they count as open.
export const is_closed_task = (item: ProfileTask): boolean => !!item.exists && is_terminal(item);

export const filter_profile_tasks = <T extends ProfileTask>(
  items: T[],
  filter: ProfileFilter
): T[] =>
  filter === 'all' ? items : items.filter((i) => is_closed_task(i) === (filter === 'closed'));

export const profile_task_counts = (items: ProfileTask[]): Record<ProfileFilter, number> => {
  const closed = items.filter(is_closed_task).length;
  return { open: items.length - closed, closed, all: items.length };
};

// Open first; a student with nothing open lands on All so the panel is never empty.
export const default_profile_filter = (items: ProfileTask[]): ProfileFilter =>
  profile_task_counts(items).open > 0 ? 'open' : 'all';

export interface StatusChip {
  tone: Tone;
  label: string;
}

// One tone per status, as on the list (approved decisions).
export const PROFILE_STATUS_CHIPS: Record<string, StatusChip> = {
  pending: { tone: 'neutral', label: 'Pending' },
  draft: { tone: 'warning', label: 'Draft' },
  submitted: { tone: 'success', label: 'Completed' },
  skipped: { tone: 'outline', label: 'Skipped' },
  auto_completed: { tone: 'outline', label: 'Auto completed' },
  auto_closed: { tone: 'info', label: 'Auto closed' }
};

export const profile_status_chip = (item: ProfileTask): StatusChip => {
  if (!item.exists) return { tone: 'outline', label: 'Not created' };
  return PROFILE_STATUS_CHIPS[item.status || ''] || PROFILE_STATUS_CHIPS.pending;
};

// "Instant" or "Day N"; blank for placeholders, which have no rule run yet.
export const profile_rule_text = (item: ProfileTask): string => {
  if (!item.exists) return '';
  if (item.is_instant) return 'Instant';
  return typeof item.rule_day === 'number' ? `Day ${item.rule_day}` : '';
};

// "L2 · Lead · Created 27 Oct, 4:30 PM · 2 hr ago" in IST, the zone the due time is in.
export const profile_meta_line = (item: ProfileTask, nowMs: number): string => {
  if (!item.exists) return 'Not created yet';
  return [item.level, item.is_lead ? 'Lead' : '', createdListLine(item.created_at_iso, null, nowMs)]
    .filter(Boolean)
    .join(' · ');
};
